import { type CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';

import { type FormContextCalendar } from '@blaze-shared/elements-domain';
import { urlUtils } from '@blaze-shared/platform-util';
import { isLinkedInlineColumn } from '@blaze-shared/schema-domain';
import { type DatasetColumn, isDatasetLinkValue } from '@blaze-shared/schema-domain';
import {
  emailRegex,
  getCountryFromLocale,
  isNullish,
  isZipCodeValid,
  regexpMatch,
  userBrowserCountry,
} from '@blaze-shared/shared-util';

import { type FormValidationModelField } from './form-validation-model';
import {
  type PropEditorFieldValidation,
  validatePropEditorRange,
} from './prop-editor-validation-rules';

/** Everything about the form, beyond the column itself, that its value is judged against. */
export interface ColumnValidationContext {
  /** The column's entry in the form's model, absent when the form does not list it. */
  modelField: FormValidationModelField | undefined;
  /** Cross-column rules the container imposes, e.g. a calendar's start/end pair. */
  formContext: FormContextCalendar | undefined;
  /** Accounts links are filled in by the backend, so they are never demanded here. */
  accountsTableName: string | undefined;
  /** Range rules the author typed into the properties editor for this column's field. */
  propEditorValidation: PropEditorFieldValidation | undefined;
}

/**
 * Validates one column of a form's value and returns the first rule it breaks, or
 * `undefined` when the value is acceptable.
 */
export function validateColumn(
  column: DatasetColumn,
  columnName: string,
  value: Record<string, any>,
  { modelField, formContext, accountsTableName, propEditorValidation }: ColumnValidationContext,
): Record<string, boolean | string | number> | undefined {
  if (isLinkedInlineColumn(column)) {
    return undefined;
  }

  const rowValue = normalizeColumnValue(column, value?.[columnName]);
  const hasValue = hasColumnValue(column, rowValue);

  if (
    modelField?.visible &&
    (modelField?.dynamicallyRequired || shouldValidateRequiredField(column, accountsTableName)) &&
    !hasValue
  ) {
    return { required: true };
  }

  if (hasValue && !isColumnValueFormatValid(column, rowValue)) {
    return { valueFormat: true };
  }

  if (modelField?.visible && modelField?.dynamicallyInvalid) {
    return { invalid: modelField.dynamicallyInvalid };
  }

  const rangeErrors = validatePropEditorRange(value?.[columnName], propEditorValidation);
  if (hasValue && Object.values(rangeErrors).length) {
    return rangeErrors;
  }

  if (hasValue && breaksCalendarOrder(columnName, value, formContext)) {
    return { endAfterStart: true };
  }

  return undefined;
}

/** A required column is validated unless the backend is the one filling it in. */
export function shouldValidateRequiredField(
  column: DatasetColumn,
  accountsTableName: string | undefined,
): boolean {
  if (!column.isRequired) {
    return false;
  }
  // don't validate accounts column - the value is filled on the BE
  if (accountsTableName && column.link?.tableName === accountsTableName) {
    return false;
  }

  return true;
}

/** Checks the value against the column's format (URL, e-mail, phone number, zip code). */
export function isColumnValueFormatValid(
  column: DatasetColumn,
  columnValue: string | number,
): boolean {
  const value = columnValue.toString();
  switch (column.format?.formatType) {
    case 'Url':
      return urlUtils.isValidUrl(value);
    case 'Email':
      return regexpMatch(emailRegex, value);
    case 'PhoneNumber': {
      try {
        return parsePhoneNumberWithError(
          value,
          (getCountryFromLocale(column?.format?.culture) || userBrowserCountry()) as CountryCode,
        ).isPossible();
      } catch {
        return false;
      }
    }
    case 'ZipCode':
      return isZipCodeValid(value, getCountryFromLocale(column?.format?.culture));
    default:
      return true;
  }
}

/** Single-value columns accept the first item of an array; multi-value columns wrap a scalar. */
function normalizeColumnValue(column: DatasetColumn, rowValue: any): any {
  if (!column.multipleValues && column.dataType !== 'Array') {
    return Array.isArray(rowValue) ? rowValue[0] : rowValue;
  }
  return !isNullish(rowValue) && !Array.isArray(rowValue) ? [rowValue] : rowValue;
}

/** A linked value only counts as filled in once it points at a row. */
function hasColumnValue(column: DatasetColumn, rowValue: any): boolean {
  if (!column.multipleValues && column.dataType !== 'Array') {
    if (!!rowValue && isDatasetLinkValue(rowValue)) {
      return !!rowValue.rowId;
    }
    return !!rowValue || rowValue === 0 || rowValue === false;
  }

  if (!!rowValue && Array.isArray(rowValue) && rowValue.some((item) => isDatasetLinkValue(item))) {
    return rowValue.every((item) => isDatasetLinkValue(item) && !!item.rowId);
  }
  return !!rowValue && !!rowValue.length;
}

/** A calendar's end date has to come after its start date. */
function breaksCalendarOrder(
  columnName: string,
  value: Record<string, any>,
  formContext: FormContextCalendar | undefined,
): boolean {
  if (
    formContext?.type !== 'calendar' ||
    !formContext.startColumn ||
    formContext.endColumn !== columnName
  ) {
    return false;
  }

  const start = value[formContext.startColumn];
  const end = value[formContext.endColumn];
  return !!start && !!end && new Date(end) <= new Date(start);
}
